import { createContext, useState, useEffect, type ReactNode } from 'react'
import es from '../lang/locales/es-no.json'
import en from '../lang/locales/en-no.json'
import type { Locale, I18nContextValue } from './I18nContext.tsx'

type Dictionary = Record<string, string>

const dictionaries: Record<Locale, Dictionary> = {
  es: es as Dictionary,
  en: en as Dictionary,
}

const I18nContext = createContext<I18nContextValue>({
  locale: 'es',
  setLocale: () => {},
  lang: (key) => key,
  missingKeys: {},
})

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    return (localStorage.getItem('lang') as Locale) || 'es'
  })

  useEffect(() => {
    localStorage.setItem('lang', locale)
  }, [locale])

  const setLocale = (l: Locale) => setLocaleState(l)

  // Recuperamos las missing keys del localStorage
  const getMissingKeys = (): Record<string, string> => {
    try {
      return JSON.parse(localStorage.getItem('missingTranslations') || '{}')
    } catch {
      return {}
    }
  } 

  const lang = (key: string, params?: Record<string, any>): ReactNode => {
    const dict = dictionaries[locale] || {}
    const template = dict[key] ?? key

    // if (process.env.NODE_ENV === 'development' && !(key in (dictionaries["es"] || {}))) {
    //   const missing = getMissingKeys()
    //   if (!(key in missing)) {
    //     missing[key] = key
    //     localStorage.setItem('missingTranslations', JSON.stringify(missing, null, 2))
    //   }
    // }

    if (!params) return template

    const parts = template.split(/(\{.*?\})/g)

    return parts.map((part) => {
      const match = part.match(/^\{(.*)\}$/)
      if (match) {
        const paramName = match[1]
        const value = params[paramName]
        
        if (typeof value === 'function') return value()
        if (value !== undefined) return value
        
        return part
      }
      return part
    })
  }

  return (
    <I18nContext.Provider
      value={{
        locale,
        setLocale,
        lang,
        missingKeys: getMissingKeys(),
      }}
    >
      {children}
    </I18nContext.Provider>
  )
}

export default I18nContext
